'use strict';

import { Component, OnInit, ViewChild, ElementRef, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { NgbNavModule, NgbNavChangeEvent, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbNav, NgbNavItem, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from 'src/app/api.service';
import { OrganisationService } from 'src/app/organisation.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import * as d3 from 'd3';
import { Helpers } from 'src/app/app.helpers';
import { JobAnalysis, RotorType, StatorType } from 'src/app/app.jobanalysis';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { RxStompService } from '../../rx-stomp.service';
import { Message } from '@stomp/stompjs';
import config from '../../../../auth_config.json';
import { HttpErrorResponse } from '@angular/common/http';
import { MatExpansionModule, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { FormsModule } from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import { SafePipe } from '../../safe.pipe';
import { JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { CommonModule } from '@angular/common'; // <-- Import this!
import { NetlistViewerComponent } from '../../components/netlist-viewer/netlist-viewer.component';
// Define a class to hold the slot data



@Component({
  selector: 'app-geometry',
  templateUrl: './geometry.component.html',
  styleUrls: ['./geometry.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, FormsModule, RouterLink, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, NgClass, NgbNav, NgbNavItem, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgStyle, NgbNavOutlet, SafePipe, JsonEditorComponent, CommonModule, NetlistViewerComponent]
})
export class GeometryComponent implements OnInit {
  public editorOptions: JsonEditorOptions;
  visUrl = config.visUri + '?id=1';
  materials: Array<any>;
  hasApiError = false;
  apiError: string;
  rotorUpdateFn: any;
  statorUpdateFn: any;
  job: any;
  isDirty: boolean = false;
  isWorking: boolean = false;
  receivedLogs: string[] = [];
  timeProgress: string;
  solveProgress: string;
  rollupProgress: number = 0;
  active = 1;
  inContextOrgId: string;
  doMechanical: boolean = false;
  doThermal: boolean = false;
  activeTab: string = '1';
  diagramNetlist: any;

  // @ts-ignore, to suppress warning related to being undefined
  private logSubscription: Subscription;
  // @ts-ignore, to suppress warning related to being undefined
  private statusSubscription: Subscription;
  // @ts-ignore, to suppress warning related to being undefined
  private progressSubscription: Subscription;
  // @ts-ignore, to suppress warning related to being undefined
  private artifactSubscription: Subscription;

  analysisMap: Array<JobAnalysis> = [
    {
      name: 'electromagnetic',
      doStator: true,
      doRotor: true,
      description: 'Electromagnetic',

    },
    {
      name: 'mechanical',
      doStator: false,
      doRotor: true,
      description: 'Mechanical',
    },
    { name: 'thermal', doStator: true, doRotor: false, description: 'Thermal' },
  ];

  rotorTypesMap: Array<RotorType> = [
    { name: 'spmbrl', description: 'Surface Mounted Breadloaf' },
    { name: 'spmarc', description: 'Surface Mounted Arc' },
    { name: 'ipmv', description: 'Interior Mounted V' },
    { name: 'insetarc', description: 'Inset Mounted Arc' },
    { name: 'ipmbar', description: 'Interior Mounted Bar' },
    {
      name: 'eesm',
      description: 'Electrically Excited Consequent Pole Synchronous Motor',
    },
    { name: 'swrel', description: 'Switched Reluctance (Not implemented)' },
    { name: 'varel', description: 'Variable Reluctance (Not implemented)' },
    {
      name: 'sqrlcg',
      description: 'Squirrel Cage Induction (Not implemented)',
    },
  ];

  statorTypesMap: Array<StatorType> = [
    { name: 'fscwseg', description: 'Concentrated wound tooth segmented' },
    { name: 'dwpss', description: 'Distributed Hairpin' },
    { name: 'dwpst', description: 'Distributed Wound Paralel Sided Tooth' },
  ];

  // Default selections to first item in each list
  selectedAnalysis: JobAnalysis = this.analysisMap[0];
  activeRotorType: RotorType = this.rotorTypesMap[0];
  activeStatorType: StatorType = this.statorTypesMap[0];

  public svg!: d3.Selection<SVGGElement, unknown, null, undefined>;
  @ViewChild('svgcontainer') svgcontainer: ElementRef;

  @ViewChild(JsonEditorComponent, { static: false }) editor: JsonEditorComponent;

  constructor(
    private api: ApiService,
    private orgService: OrganisationService,
    private rxStompService: RxStompService,
    private configFactory: AuthClientConfig,
    public helpers: Helpers,
    public analysis: JobAnalysis,
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
  ) {

    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.modes = ['code', 'text', 'tree'];

    // this.editorOptions.modes = ['code', 'text', 'tree', 'view']; // set all allowed modes
  }

  setJob(job: any): void {
    this.job = job;
    this.diagramNetlist = job.netlist;
  }

  initJob(targetJobId: string | null): void {

    if (!targetJobId) {
      return;
    }

    this.hasApiError = false;
    this.isWorking = true;

    this.api.getJobsId$(targetJobId).subscribe({
      next: (res) => {
        var isExampleJob = this.helpers.getIsExampleJobId(targetJobId);

        // First time into page
        if (this.job) {
          var isExistingJob = this.helpers.getJobIsExisting(res);
          if (isExistingJob) {
            res.id = this.job.id;
          }

          res.title = this.job.title;

          // Set dirty if this isn't an existing job
          this.setIsIdle(!isExistingJob || isExampleJob);
        } else {

          // Set dirty if we're looking at an example job
          this.setIsIdle(isExampleJob);
        }

        this.setJob(res);

        if (this.job.title == null || this.job.title == '') {
          this.job.title = 'Untitled';
        }

        if (!isExampleJob) {
          this.subscribeToUpdates();
        }

        var projectType = this.job.type.split('_', 3);
        this.selectedAnalysis = this.analysisMap.find((a) => {
          return a.name == projectType[0];
        });

        if (this.selectedAnalysis.doRotor) {
          this.activeRotorType = this.rotorTypesMap.find((r) => {
            return r.name == projectType[1];
          });
        }

        if (this.selectedAnalysis.doStator) {
          this.activeStatorType = this.statorTypesMap.find((s) => {
            return s.name == projectType[2];
          });
        }

        var geometries = this.helpers.job2geometry(this.job);

        var rotorName = this.selectedAnalysis.doRotor
          ? this.activeRotorType.name
          : 'none';
        var statorName = this.selectedAnalysis.doStator
          ? this.activeStatorType.name
          : 'none';

        var winding_param = this.helpers.job2winding(this.job);

        const geocalls = forkJoin({
          rotor: this.api.postGetRotorPartsGeometry$(rotorName, geometries.rotor),
          stator: this.api.postGetStatorPartsGeometry$(statorName, geometries.stator),
          winding: this.api.postGetWinding$(rotorName, statorName, winding_param),
        });

        geocalls.subscribe({
          next: (res) => {
            [this.rotorUpdateFn, this.statorUpdateFn] = this.initialise_svg(
              res.rotor,
              res.stator,
              res.winding
            );
          },
          error: (error) => this.handleError(error),
        });

        // Iterate through the job's artifact
        if (this.job.artifacts) {
          this.job.artifacts.forEach((artifact) => {
            if (
              artifact.url.startsWith('https') &&
              artifact.url.endsWith('.vtu')
            ) {
              this.setVisArtifact(artifact);
            }
          });
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  // This should happen first
  ngOnInit(): void {
    this.orgService.currentOrg$.subscribe((org) => {
      this.inContextOrgId = org && org.id ? org.id : '';
    });

    var jobId = this.route.snapshot.paramMap.get('id');
    if (jobId == '0') {
      jobId = `${this.selectedAnalysis.name}_${this.activeRotorType.name}_${this.activeStatorType.name}`;
    }

    // Load materials
    this.api.getMaterials$().subscribe({
      next: (res) => {
        this.materials = res;
      },
      error: (error) => this.handleError(error),
    });

    this.initJob(jobId);
  }

  updateAnalysisType(analysis: any): void {
    var rotorTypeName = analysis.doRotor ? this.activeRotorType.name : 'none';
    var statorTypeName = analysis.doStator
      ? this.activeStatorType.name
      : 'none';
    var jobId = `${analysis.name}_${rotorTypeName}_${statorTypeName}`;
    this.initJob(jobId);
  }

  updateRotorType(rotorType: any): void {
    var statorTypeName = this.selectedAnalysis.doStator
      ? this.activeStatorType.name
      : 'none';
    var jobId = `${this.selectedAnalysis.name}_${rotorType.name}_${statorTypeName}`;
    this.initJob(jobId);
  }

  updateStatorType(statorType: any): void {
    var rotorTypeName = this.selectedAnalysis.doRotor
      ? this.activeRotorType.name
      : 'none';
    var jobId = `${this.selectedAnalysis.name}_${rotorTypeName}_${statorType.name}`;
    this.initJob(jobId);
  }

  updateGeometries(): void {
    this.hasApiError = false;
    this.isDirty = true;

    var geometries = this.helpers.job2geometry(this.job);
    var winding_param = this.helpers.job2winding(this.job);

    var rotorName = this.selectedAnalysis.doRotor
      ? this.activeRotorType.name
      : 'none';
    var statorName = this.selectedAnalysis.doStator
      ? this.activeStatorType.name
      : 'none';

    const geocalls = forkJoin({
      rotor: this.api.postGetRotorPartsGeometry$(rotorName, geometries.rotor),
      stator: this.api.postGetStatorPartsGeometry$(statorName, geometries.stator),
      winding: this.api.postGetWinding$(rotorName, statorName, winding_param),
    });

    geocalls.subscribe({
      next: (res) => {
        this.rotorUpdateFn(res.rotor, res.stator, res.winding);
        this.statorUpdateFn(res.stator, res.rotor, res.winding);
      },
      error: (error) => this.handleError(error),
    });
  }

  setIsIdle(isDirty?: boolean): void {
    this.isWorking = false;
    this.isDirty = (typeof isDirty !== 'undefined') && isDirty
  }

  createOrUpdateJob(): void {
    this.hasApiError = false;
    this.isWorking = true;

    // Reunite the job with its netlist from the editor
    this.job.netlist = this.editor.get();

    var isExampleJob = this.job.id == this.job.type;
    if (isExampleJob) {
      this.job.id = null;
      this.api.postJobs$(this.inContextOrgId, this.job).subscribe({
        next: (res) => {
          this.setJob(res);
          this.setIsIdle();
          this.subscribeToUpdates();
          this.router.navigateByUrl('/geometry/' + this.job.id);
        },
        error: (error) => this.handleError(error),
      });
    } else {
      this.api.putJobsId$(this.job.id, this.job).subscribe({
        next: (res) => {
          this.setJob(res);
          this.setIsIdle();
          this.subscribeToUpdates();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  queueJob(): void {
    this.hasApiError = false;
    this.isWorking = true;

    this.api.putJobsIdQueue$(this.job.id).subscribe({
      next: (res) => {
        this.setJob(res);
        this.setIsIdle();
        this.subscribeToUpdates();
      },
      error: (error) => this.handleError(error),
    });

    if (this.doMechanical) {
      this.api.copyAsAnalysisType$(this.inContextOrgId, this.job, 'mechanical').subscribe({
        next: (res) => {
          this.api.putJobsIdQueue$(res.id).subscribe({
            next: () => { },
            error: (error) => this.handleError(error),
          });
        },
        error: (error) => this.handleError(error),
      });
    }

    if (this.doThermal) {
      this.api.copyAsAnalysisType$(this.inContextOrgId, this.job, 'thermal').subscribe({
        next: (res) => {
          this.api.putJobsIdQueue$(res.id).subscribe({
            next: () => { },
            error: (error) => this.handleError(error),
          });
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  copyJob(): void {
    this.hasApiError = false;
    this.isWorking = true;

    this.api.copyJobs$(this.inContextOrgId, this.job).subscribe({
      next: (res) => {
        this.setJob(res);
        this.setIsIdle();
        this.router.navigateByUrl('/geometry/' + this.job.id);
        this.unsubscribeFromUpdates();
        this.subscribeToUpdates();
      },
      error: (error) => this.handleError(error),
    });
  }

  deleteJob(id: string, delete_type: number) {

    var msg = "";

    if (delete_type == 0) {
      var msg = "Job will be sent to the bin and permanently deleted in a few days. Are you sure?";
    }

    if (delete_type == 10) {
      var msg = "This will permanently delete the job and all associated data. Are you sure?";
    }

    // If msg is populated
    if (msg != "") {
      var doDelete = confirm(msg);
      if (!doDelete) {
        return;
      }
    }

    this.api.deleteJobsId$(id, delete_type).subscribe({
      next: (res) => {

        this.hasApiError = false;

        // If we're restoring
        if (delete_type == 20) {
          this.setJob(res);
          this.setIsIdle();
          this.subscribeToUpdates();
        } else {
          this.router.navigateByUrl('/jobs');
        }
      },
      error: (error) => {
        this.hasApiError = true;
        this.apiError = error.message;
      },
    });
  }

  resetNetlist(id: string) {
    this.hasApiError = false;
    this.isWorking = true;
    this.api.postGetJobNetlist$(this.job).subscribe({
      next: (res) => {
        this.job.netlist = res;
        this.diagramNetlist = res;
        this.setIsIdle(true);
      },
      error: (error) => this.handleError(error),
    });
  }

  initialise_svg(rotor, stator, winding): any {

    const childElements = this.svgcontainer.nativeElement.children;
    for (let child of childElements) {
      this.renderer.removeChild(this.svgcontainer.nativeElement, child);
    }

    // Get the div dimensions
    var divHeight = this.svgcontainer.nativeElement.offsetHeight;
    var divWidth = this.svgcontainer.nativeElement.offsetWidth;

    // Set the canvas dimensions to same as the div
    let canvasWidth = divWidth;
    let canvasHeight = divHeight;

    // Set the drawing dimensions to the smaller of the two
    let drawingWidth = Math.min(canvasWidth, canvasHeight);
    let drawingHeight = drawingWidth;

    let padding = 35;

    let sx = d3
      .scaleLinear()
      .domain([0, 150])
      .range([0 + padding, drawingWidth - padding]); //500 pixels

    // radial scale
    let sr = d3
      .scaleLinear()
      .domain([0, 150])
      .range([0, drawingWidth - 2 * padding]);

    // Create a scale: transform value in pixel
    let sy = d3
      .scaleLinear()
      .domain([-150, 0])
      .range([0 + padding, drawingHeight - padding]);

    var ttipdiv = d3.select("body").append("div")
      .attr("class", "tooltip")
      .style("opacity", 0);


    let svg = d3
      .select(this.svgcontainer.nativeElement)
      .append('svg')
      .attr('width', canvasWidth)
      .attr('height', canvasHeight)
      .call(
        d3.zoom().on('zoom', function zoomed({ transform }, d) {
          svg.attr('transform', transform);
        })
      )
      .append('g')
      .style("opacity", 0);

    // Add the x Axis
    svg
      .append('g')
      .attr('transform', 'translate(' + 0 + ',' + sy(0) + ')')
      .call(d3.axisBottom(sx));

    // Add the y Axis
    svg
      .append('g')
      .attr('transform', 'translate(' + sx(0) + ',' + 0 + ')')
      .call(d3.axisLeft(sy));

    let symmetry: number = 2; // default symmetry
    let in_phases: string[];
    // If we don't have a winding object, we can't color the winding phase colors
    // if we do have the winding object, we can make a winding array and color the winding phases
    // The phases are lower case letter.
    // we also set the symmetry to the winding symmetry
    if (winding !== null) {
      var winding_array = this.helpers.winding2array(winding);
      // console.log('winding_array', winding_array);
      in_phases = Array.from({ length: winding.number_phases }, (_, i) => String.fromCharCode(97 + i)); // 97 is the ASCII value for 'a'
      var phasecolors = d3.scaleOrdinal(in_phases, d3.schemeTableau10).unknown(null);
      symmetry = winding.symmetry;
    } else {
      var phasecolors = d3.scaleOrdinal(['probably', 'wont', 'matter', 'what', 'is', 'here'], d3.schemeTableau10).unknown(null);
    }


    // If we don't have a rotor object, e.g. a Stator Thermal simulation, we can't draw the rotor so we draw the rotor hint circles
    if (rotor == null) {
      draw_part_hint_circle(stator.geometry.geometries[0].neighbouring_part_hint, 0, 'rotor');
      draw_part_hint_circle(stator.geometry.geometries[0].neighbouring_part_hint, 1, 'rotor');
      symmetry = stator.number_slots * 2; // a single half tooth.
    }
    else {

      // TODO - find out why this is stupid
      // If symmetry is not defined
      if (symmetry == null) {
        symmetry = 2; // default symmetry
      }

      // draw the rotor
      let rotor_array = Array.from({ length: rotor.number_poles * 2 / symmetry }, (_, i) => i);
      for (let i = 0; i < rotor.geometry.geometries.length; i++) {
        svg
          .selectAll('whatever')
          .data(rotor_array)
          .enter()
          .append('path')
          .attr('d', function (d) {
            return draw_part(d3.path(), rotor.geometry.geometries[i].points, rotor.geometry.geometries[i].path);
          })
          .classed('rotor', true)
          .classed(rotor.geometry.geometries[i].type, true)
          .classed(rotor.geometry.geometries[i].classes, true)
          .attr('transform', function (d) {
            if (d % 2 === 1) {
              return 'rotate(' + (d * -180 / rotor.number_poles) + ',' + sx(0) + ',' + sy(0) + ')';
            } else {
              return 'translate(0, ' + sy(0) + ') scale(1, -1) translate(0, ' + -sy(0) + ') rotate(' + ((1 + d) * 180 / rotor.number_poles) + ',' + sx(0) + ',' + sy(0) + ')';
            }
          })
          .on('mouseover', function (event, d) {
            d3.select(this).transition()
              .duration('50')
              .attr('opacity', '.85');
            //Makes the new div appear on hover:
            let area = 0;
            for (let i = 0; i < rotor.geometry.geometries.length; i++) {
              if (this.className.baseVal.includes(rotor.geometry.geometries[i].type)) {
                area = rotor.geometry.geometries[i].area;
                break;
              }
            }
            ttipdiv.transition()
              .duration(50)
              .style("opacity", 1);
            ttipdiv.html(d + " <br>" + this.className.baseVal + " <br> Material: <br> Area: " + area.toFixed(1) + "mm²<br> Volume: <br> Mass: ")
              .style("left", (event.pageX + 10) + "px")
              .style("top", (event.pageY - 15) + "px");
          })
          .on('mouseout', function (event, d) {
            d3.select(this).transition()
              .duration('50')
              .attr('opacity', '1');
            //Makes the new div disappear:
            ttipdiv.transition()
              .duration('50')
              .style("opacity", 0);
          });
      }
    }

    if (stator == null) {
      draw_part_hint_circle(rotor.geometry.geometries[0].neighbouring_part_hint, 0, 'stator');
      draw_part_hint_circle(rotor.geometry.geometries[0].neighbouring_part_hint, 1, 'stator');
      symmetry = 4; // a quater rotor to align x, and y axis with the boundary conditions.
    }
    else {
      let stator_array = Array.from({ length: stator.number_slots * 2 / symmetry }, (_, i) => i);
      for (let i = 0; i < stator.geometry.geometries.length; i++) {
        svg
          .selectAll('whatever')
          .data(stator_array)
          .enter()
          .append('path')
          .attr('d', function (d) {
            return draw_part(d3.path(), stator.geometry.geometries[i].points, stator.geometry.geometries[i].path);
          })
          .classed('stator', true)
          .classed(stator.geometry.geometries[i].type, true)
          .classed(stator.geometry.geometries[i].classes, true)
          .attr('transform', function (d) {
            if (d % 2 === 1) {
              return 'rotate(' + (d * -180 / stator.number_slots) + ',' + sx(0) + ',' + sy(0) + ')';
            } else {
              return 'translate(0, ' + sy(0) + ') scale(1, -1) translate(0, ' + -sy(0) + ') rotate(' + ((1 + d) * 180 / stator.number_slots) + ',' + sx(0) + ',' + sy(0) + ')';
            }
          })
          .on('mouseover', function (event, d) {
            d3.select(this).transition()
              .duration('50')
              .attr('opacity', '.85');
            //Makes the new div appear on hover:
            let area = 0;
            for (let i = 0; i < stator.geometry.geometries.length; i++) {
              if (this.className.baseVal.includes(stator.geometry.geometries[i].type)) {
                area = stator.geometry.geometries[i].area;
                break;
              }
            }
            ttipdiv.transition()
              .duration(50)
              .style("opacity", 1);
            ttipdiv.html(d + " <br>" + this.className.baseVal + " <br> Material: <br> Area: " + area.toFixed(1) + "mm²<br> Volume: <br> Mass: ")
              .style("left", (event.pageX + 10) + "px")
              .style("top", (event.pageY - 15) + "px");
          })
          .on('mouseout', function (event, d) {
            d3.select(this).transition()
              .duration('50')
              .attr('opacity', '1');
            //Makes the new div disappear:
            ttipdiv.transition()
              .duration('50')
              .style("opacity", 0);
          })
      }
      if (winding !== null) {
        // if the stator doesn't have number_pins, it is equal to 1
        let num_conductors = stator.number_pins ? stator.number_pins : 1;
        for (let conductor_idx = 0; conductor_idx < num_conductors; conductor_idx++) {
          svg.selectAll('.conductor_' + conductor_idx)
            .style('fill', function (d) {
              let slot: number = Math.floor((d % (winding.number_slots * 2)) / 2);
              let layer: number;
              if (num_conductors == 1 && winding.number_layers == 2) {
                layer = d % winding.number_layers;
              } else {
                layer = ((conductor_idx / stator.number_pins) < (winding.number_layers - 1) / 2) ? 1 : 0;
              }
              let phase: string = winding_array[slot][layer];
              // same color but darker for Capital letters
              return (phase === phase.toLowerCase() ? phasecolors(winding_array[slot][layer]) : d3.color(phasecolors(winding_array[slot][layer].toLowerCase())).darker(0.75));
            });
        }
      }
    }

    svg.transition().duration(500).style("opacity", 1)

    function draw_part_hint_circle(data, index, part) {
      svg.selectAll("whatever")
        .data([data])
        .enter()
        .append("circle")
        .attr("cx", function (d) {
          return sx(0);
        })
        .attr("cy", function (d) {
          return sy(0);
        })
        .attr("r", function (d) {
          return sr(d[index] / 2);
        })
        .attr("fill", "none")
        .attr("stroke", "blue")
        .style("stroke-width", 0.5)
        .style("stroke-dasharray", "4 1 2")
        .classed(part + "_part_hint", true)
        .classed("circles", true)
        .classed("inactive", false);
    }

    function draw_part(context, points, path) {
      console.debug('draw_part: points', points);
      console.debug('draw_part: path', path);

      context.fillStyle = 'none';
      let radius = 0,
        startAngle = 0,
        endAngle = 0,
        angle = 0;
      let anticlockwise = false;

      for (let loop_idx = 0; loop_idx < path.length; loop_idx++) {
        //let path_origin = path[loop_idx][0];
        //let current_point = path_origin;
        let first_point = points[path[loop_idx][0][0]];
        context.moveTo(sx(first_point[0]), sy(first_point[1]));

        for (
          let segment_idx = 0;
          segment_idx < path[loop_idx].length;
          segment_idx++
        ) {
          //console.debug('segment_idx', segment_idx);
          //console.debug('loop_idx', loop_idx);
          //console.debug('path[loop_idx]', path[loop_idx]);
          // console.debug(
          //   'path[loop_idx][segment_idx]',
          //   path[loop_idx][segment_idx]
          // );
          let sp = points[path[loop_idx][segment_idx][0]];
          let ep = points[path[loop_idx][segment_idx][1]];

          // console.debug('Start Point', sp);
          // console.debug('End Point', ep);

          if (path[loop_idx][segment_idx][2] != -1) {
            let cp = points[path[loop_idx][segment_idx].at(2)];
            // console.debug('Center point', cp);

            // this is an arc
            startAngle = Math.atan2(sp[1] - cp[1], sp[0] - cp[0]);
            startAngle = startAngle < 0 ? startAngle + 2 * Math.PI : startAngle;
            endAngle = Math.atan2(ep[1] - cp[1], ep[0] - cp[0]);
            endAngle = endAngle < 0 ? endAngle + 2 * Math.PI : endAngle;
            angle = endAngle - startAngle;

            let external_loop = loop_idx === 0;

            //anticlockwise = external_loop ? startAngle < endAngle : startAngle > endAngle;
            // check the cross product of the vectors to find the smallest arc direction
            anticlockwise =
              (sp[0] - cp[0]) * (ep[1] - cp[1]) -
              (sp[1] - cp[1]) * (ep[0] - cp[0]) <
              0;

            radius = Math.sqrt((sp[1] - cp[1]) ** 2 + (sp[0] - cp[0]) ** 2);
            // console.debug('external loop', external_loop);
            // console.debug(
            //   'anticlockwise = %s, startAngle = %s, endAngle = %s, radius = %s, cp = %s',
            //   anticlockwise,
            //   (startAngle * 180) / Math.PI,
            //   (endAngle * 180) / Math.PI,
            //   radius,
            //   cp
            // );
            context.arc(
              sx(cp[0]),
              sy(cp[1]),
              sr(radius),
              startAngle,
              endAngle,
              anticlockwise
            );
          } else {
            context.lineTo(sx(ep[0]), sy(ep[1]));
            // Will this break if the last segment is an arc?
          } //context.closePath();
        }
      }
      return context;
    }

    let rotorUpdateFn = function update_rotor(rotor, stator, winding) {
      console.info('update_rotor');
      console.info('windings', winding);
      svg.selectAll('circle.stator_part_hint').classed('inactive', true);

      if (rotor == null) {
        svg.selectAll('path.rotor_lamination').classed('inactive', true);
        svg.selectAll('path.rotor_magnet').classed('inactive', true);
        svg.selectAll('path.rotor_air_L').classed('inactive', true);
        svg.selectAll('path.rotor_air_R').classed('inactive', true);
        svg.selectAll('path.rotor_banding').classed('inactive', true);
        svg.selectAll('path.rotor_winding').classed('inactive', true);
        svg.selectAll('circle.part_hint').classed('inactive', true);
        return;
      }

      if (stator == null) {
        draw_part_hint_circle(rotor.geometry.geometries[0].neighbouring_part_hint, 0, 'stator');
        draw_part_hint_circle(rotor.geometry.geometries[0].neighbouring_part_hint, 1, 'stator');
      }
      let rotor_array = Array.from({ length: rotor.number_poles * 2 / winding.symmetry }, (_, i) => i);
      for (let i = 0; i < rotor.geometry.geometries.length; i++) {
        svg
          .selectAll('path.' + rotor.geometry.geometries[i].type)
          .data(rotor_array)
          .join(enter => enter.append('path')
            .classed(rotor.geometry.geometries[i].type, true)
            .classed(rotor.geometry.geometries[i].classes, true),
            update => update,
            exit => exit.remove())
          .classed('inactive', false)
          .transition()
          .delay(50)
          .duration(1000)
          .attr('d', function (d) {
            return draw_part(d3.path(), rotor.geometry.geometries[i].points, rotor.geometry.geometries[i].path);
          })
          .attr('transform', function (d) {
            if (d % 2 === 1) {
              return 'rotate(' + (d * -180 / rotor.number_poles) + ',' + sx(0) + ',' + sy(0) + ')';
            } else {
              return 'translate(0, ' + sy(0) + ') scale(1, -1) translate(0, ' + -sy(0) + ') rotate(' + ((1 + d) * 180 / rotor.number_poles) + ',' + sx(0) + ',' + sy(0) + ')';
            }
          });
      }
    };

    let statorUpdateFn = function update_stator(stator, rotor, winding) {
      console.log('update_stator');
      console.info('windings', winding);
      var winding_array = this.helpers.winding2array(winding);
      console.info('winding_array', winding_array);
      svg.selectAll('circle.rotor_part_hint').classed('inactive', true);

      if (winding !== null) {
        var winding_array = this.helpers.winding2array(winding);
        console.info('winding_array', winding_array);
        in_phases = Array.from({ length: winding.number_phases }, (_, i) => String.fromCharCode(97 + i)); // 97 is the ASCII value for 'a'
        var phasecolors = d3.scaleOrdinal(in_phases, d3.schemeTableau10).unknown(null);
        symmetry = winding.symmetry;
      }

      if (stator == null) {
        svg.selectAll('path.stator_lamination').classed('inactive', true);
        svg.selectAll('path.stator_slot_wedge').classed('inactive', true);
        svg.selectAll('path.stator_slot_area').classed('inactive', true);
        svg.selectAll('path.stator_slot_liner').classed('inactive', true);
        return;
      }

      if (rotor == null) {
        draw_part_hint_circle(stator.geometry.geometries[0].neighbouring_part_hint, 0, 'rotor');
        draw_part_hint_circle(stator.geometry.geometries[0].neighbouring_part_hint, 1, 'rotor');
      }

      let stator_array = Array.from({ length: stator.number_slots * 2 / winding.symmetry }, (_, i) => i);
      for (let i = 0; i < stator.geometry.geometries.length; i++) {
        svg
          .selectAll('path.' + stator.geometry.geometries[i].type)
          .data(stator_array)
          .join(enter => enter.append('path')
            .classed(stator.geometry.geometries[i].type, true)
            .classed(stator.geometry.geometries[i].classes, true),
            update => update,
            exit => exit.remove())
          .classed('inactive', false)
          .transition()
          .delay(50)
          .duration(1000)
          .attr('d', function (d) {
            return draw_part(d3.path(), stator.geometry.geometries[i].points, stator.geometry.geometries[i].path);
          })
          .attr('transform', function (d) {
            if (d % 2 === 1) {
              return 'rotate(' + (d * -180 / stator.number_slots) + ',' + sx(0) + ',' + sy(0) + ')';
            } else {
              return 'translate(0, ' + sy(0) + ') scale(1, -1) translate(0, ' + -sy(0) + ') rotate(' + ((1 + d) * 180 / stator.number_slots) + ',' + sx(0) + ',' + sy(0) + ')';
            }
          });
      }
      if (winding !== null) {
        // if the stator doesn't have number_pins, it is equal to 1
        let num_conductors = stator.number_pins ? stator.number_pins : 1;
        for (let conductor_idx = 0; conductor_idx < num_conductors; conductor_idx++) {
          svg.selectAll('.conductor_' + conductor_idx)
            .style('fill', function (d) {
              let slot: number = Math.floor((d % (winding.number_slots * 2)) / 2);
              let layer: number;
              if (num_conductors == 1 && winding.number_layers == 2) {
                layer = d % winding.number_layers;
              } else {
                layer = ((conductor_idx / stator.number_pins) < (winding.number_layers - 1) / 2) ? 1 : 0;
              }
              let phase: string = winding_array[slot][layer];
              // same color but darker for Capital letters
              return (phase === phase.toLowerCase() ? phasecolors(winding_array[slot][layer]) : d3.color(phasecolors(winding_array[slot][layer].toLowerCase())).darker(0.75));
            });
        }
      }
    };

    return [rotorUpdateFn, statorUpdateFn];
  };

  subscribeToUpdates() {
    var isSavedJob = this.helpers.getJobIsExisting(this.job);
    if (!isSavedJob) {
      return;
    }

    if (!this.logSubscription) {
      console.info('Subscribing to log updates ...');
      this.logSubscription = this.rxStompService
        .watch(`/topic/${this.job.id}.#`)
        .subscribe((msg: Message) => {
          this.receivedLogs.push(msg.body);
        });
    }

    if (!this.statusSubscription) {
      console.info('Subscribing to status updates ...');
      this.statusSubscription = this.rxStompService
        .watch(`/topic/${this.job.id}.status`)
        .subscribe((msg: Message) => {
          this.job.status = parseInt(msg.body)
          if (this.job.status > 60) {
            this.timeProgress = ""
            this.solveProgress = ""
          }
        });
    }

    if (!this.progressSubscription) {
      console.info('Subscribing to progress updates ...');
      this.progressSubscription = this.rxStompService
        .watch(`/topic/${this.job.id}.*.*.progress`)
        .subscribe((msg: Message) => {

          var json = msg.body.substring(msg.body.indexOf('{'), msg.body.lastIndexOf('}') + 1);
          var progress = JSON.parse(json);

          this.rollupProgress = 100 - progress.remaining;

          if (progress.remaining) {
            this.timeProgress = "~ " + progress.remaining + " " + progress.unit + " remaining";
          }

          if (progress.done) {
            this.solveProgress = "Solving step " + progress.done + " of " + progress.total;
          }
        });
    }

    if (!this.artifactSubscription) {
      console.info('Subscribing to artifact updates ...');
      this.artifactSubscription = this.rxStompService
        .watch(`/topic/${this.job.id}.artifact`)
        .subscribe((msg: Message) => {
          if (!this.job.artifacts) {
            this.job.artifacts = [];
          }

          var newArtifact = JSON.parse(msg.body);

          this.job.artifacts = this.job.artifacts.filter((a) => {
            return a.id != newArtifact.id;
          });
          this.job.artifacts.push(newArtifact);

          // If artifact URL starts with https and end with vtu, set the vis id
          if (
            newArtifact.url.startsWith('https') &&
            newArtifact.url.endsWith('.vtu')
          ) {
            this.setVisArtifact(newArtifact);
          }
        });
    }
  }

  unsubscribeFromUpdates() {
    if (this.logSubscription) {
      console.info('Unsubscribing from log updates ...');
      this.logSubscription.unsubscribe();
      this.logSubscription = null;
      this.receivedLogs = [];
    }

    if (this.statusSubscription) {
      console.info('Unsubscribing from status updates ...');
      this.statusSubscription.unsubscribe();
      this.statusSubscription = null;
    }

    if (this.progressSubscription) {
      console.info('Unsubscribing from progress updates ...');
      this.progressSubscription.unsubscribe();
      this.progressSubscription = null;
    }

    if (this.artifactSubscription) {
      console.info('Unsubscribing from artifact updates ...');
      this.artifactSubscription.unsubscribe();
      this.artifactSubscription = null;
    }
  }

  ngOnDestroy() {
    this.unsubscribeFromUpdates();
  }

  onNavChange(changeEvent: NgbNavChangeEvent) {
    console.log('Nav is changing from', changeEvent.activeId, 'to', changeEvent.nextId);

    this.activeTab = changeEvent.nextId;

    // You can add logic here, for example, to prevent the change:
    // if (changeEvent.nextId === 'item-two') {
    //   changeEvent.preventDefault();
    // }
  }

  promoteArtifact(artifact: any) {
    this.api.promoteJobArtifact$(this.job.id, artifact.id).subscribe({
      error: (error) => this.handleError(error),
    });
  }

  rollupArtifacts() {
    this.api.rollupJobArtifacts$(this.job.id).subscribe({
      error: (error) => this.handleError(error),
    });
  }

  getArtifctMimeType(artifact: any) {
    let mimeType = 'unknown';
    if (artifact.mimetype) {
      mimeType = artifact.mimetype.split('/')[0];
    }
    // Log the mime type
    console.debug('Artifact mime type: ' + mimeType);
    return mimeType;
  }

  setVisArtifact(artifact) {
    // Only update the URL if it hasn't been set for this job yet
    // This prevents the iframe from reloading when new artifacts arrive via STOMP
    if (!this.visUrl.includes(this.job.id)) {
      this.visUrl = config.visUri + '?job_id=' + this.job.id + '&index=0';
    }
  }

  toggleAnalysisType() {

    const geocalls = forkJoin({
      elec: this.api.getJobsId$('electromagnetic_none_none'),
      therm: this.api.getJobsId$('thermal_none_none'),
    });

    geocalls.subscribe({
      next: (res) => {

        var newItems = res.therm.data.filter((item) => !res.elec.data.some((item2) => item2.name === item.name));
        newItems.forEach((item) => {
          if (this.doThermal) {
            this.job.data.push(item);
          }
          else {
            this.job.data = this.job.data.filter((i) => i.name !== item.name);
          }
        });
      },
      error: (error) => this.handleError(error),
    });
  }

  handleError(error: HttpErrorResponse): void {
    this.hasApiError = true;
    this.apiError = error.error
      ? error.message + ': ' + error.error
      : error.message;
  }


  changeLog(event = null) {
    console.log(event);
    //console.log('change:', this.editor);

    this.diagramNetlist = this.editor.get();
    /**
     * Manual validation based on the schema
     * if the change does not meet the JSON Schema, it will use the last data
     * and will revert the user change.
     */
    // const editorJson = this.editor.getEditor();
    // editorJson.validate();
    //const errors = editorJson.validateSchema.errors;
    // if (errors && errors.length > 0) {
    //   console.log('Errors found');
    //   editorJson.set(this.job.netlist);
    // } else {
    this.isDirty = true;
    // }
  }
}


